/**
 * REDEX Service Tiers
 * Enterprise Software Consultancy Packages
 * 
 * Payment Structure:
 * 1. Monthly Maintenance Fee (subscription) - paid first and verified
 * 2. Package Retainer/Setup Fee - paid after subscription verified
 */

export interface ServiceTier {
  id: string;
  name: string;
  tagline: string;
  description: string;
  retainerFee: number; // One-time retainer/setup fee in cents
  monthlyFee: number; // Monthly maintenance subscription in cents
  retainerPriceId: string; // Stripe price ID for retainer
  monthlyPriceId: string; // Stripe price ID for monthly maintenance
  features: string[];
  highlights: string[];
  recommended?: boolean;
  enterprise?: boolean;
  includedHours?: string;
  supportLevel?: string;
}

export const SERVICE_TIERS: ServiceTier[] = [
  {
    id: 'starter',
    name: 'Core Site + Telephony',
    tagline: 'Modern web presence with lead capture',
    description: 'SMBs needing a modern site with basic Dialpad lead capture and telephony integration',
    retainerFee: 650000, // $6,500.00
    monthlyFee: 39995, // $399.95/month maintenance
    retainerPriceId: 'price_1ScWTlAmx3PBCGtXBGbz1GIA',
    monthlyPriceId: 'price_1ScWTlAmx3PBCGtX5sDsL2l5',
    includedHours: '10 hrs/mo',
    supportLevel: 'Email support (M-Sat)',
    features: [
      '8-12 page React site, WCAG-aligned UX',
      'Flask API with Dialpad contact creation',
      'A2P verified telephony integration',
      'SSL/TLS/HSTS security hardening',
      'Initial SEO meta & GA4/Plausible analytics',
      '10 hours/month rolling updates',
      'Managed AWS hosting (99.9% uptime SLA)',
      'Nightly encrypted backups',
      '24/7 uptime monitoring',
    ],
    highlights: [
      '10 hrs/mo updates',
      'Dialpad integration',
      'Managed hosting',
    ],
  },
  {
    id: 'professional',
    name: 'Growth Stack',
    tagline: 'Demand generation + reporting',
    description: 'Teams wanting demand gen, email deliverability, and comprehensive reporting',
    retainerFee: 990000, // $9,900.00
    monthlyFee: 39995, // $399.95/month maintenance
    retainerPriceId: 'price_1ScWTlAmx3PBCGtX9xgGwrdN',
    monthlyPriceId: 'price_1ScWTlAmx3PBCGtXdnsfwrKB',
    includedHours: '10 hrs/mo',
    supportLevel: 'Priority support',
    recommended: true,
    features: [
      'Everything in Core Site + Telephony',
      'AWS SES/SendGrid with SPF/DKIM/DMARC',
      'Google Business Profile setup/recovery',
      'Schema markup & sitemap generation',
      'Lead-source attribution tagging',
      'Content components & templates',
      'Monthly operations report',
      'Dedicated account manager',
      'Custom workflow automation',
    ],
    highlights: [
      'Email deliverability',
      'GBP optimization',
      'Monthly reporting',
    ],
  },
  {
    id: 'growth',
    name: 'Ops Suite',
    tagline: 'Admin portal with CRM runway',
    description: 'Firms needing a light admin portal with runway to EMR/CRM capabilities',
    retainerFee: 1850000, // $18,500.00
    monthlyFee: 149900, // $1,499.00/month (includes Ops Plus)
    retainerPriceId: 'price_1ScWTmAmx3PBCGtXLb2IsZwE',
    monthlyPriceId: 'price_1ScWTmAmx3PBCGtXKck5XPGA',
    includedHours: '20 hrs/mo',
    supportLevel: '4-hour response SLA',
    features: [
      'Admin portal MVP (lead inbox, notes, files)',
      'Branded Dialpad widgets',
      'Role-based access & audit logs',
      'Staged API endpoints for care-management',
      'File uploads & contact notes system',
      '20 hours/month total dev time',
      'Priority support with escalation',
      'Quarterly strategic review calls',
      'Custom integrations available',
    ],
    highlights: [
      '20 hrs/mo dev time',
      'Admin portal MVP',
      '4-hr response SLA',
    ],
  },
  {
    id: 'enterprise',
    name: 'Enterprise Transformation',
    tagline: 'Full-scale digital transformation',
    description: 'Mid-market/enterprise multi-system buildouts with dedicated engineering',
    retainerFee: 2499997, // $24,999.97 per quarter
    monthlyFee: 0, // Custom - all-inclusive quarterly
    retainerPriceId: 'price_1ScWTmAmx3PBCGtXrQ44Xx09',
    monthlyPriceId: '', // Custom - contact sales
    includedHours: 'Unlimited',
    supportLevel: '24/7 enterprise SLA',
    enterprise: true,
    features: [
      'Full access to all system capabilities',
      'Multi-rail payments & microservices',
      'Advanced analytics & blockchain tracks',
      'Strategy workshops & executive consulting',
      'Software dev + hosting + DB management',
      '24/7 priority support with dedicated line',
      'Custom integrations & white-label options',
      'Dedicated engineering team',
      'On-site training available',
      'SOC2/HIPAA compliance available',
    ],
    highlights: [
      'Unlimited dev hours',
      'Dedicated team',
      '24/7 enterprise SLA',
    ],
  },
];

// Helper functions
export function getTier(tierId: string): ServiceTier | undefined {
  return SERVICE_TIERS.find(t => t.id === tierId);
}

export function formatPrice(cents: number): string {
  if (cents === 0) return 'Custom';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
  }).format(cents / 100);
}

export function getRecommendedTier(): ServiceTier {
  return SERVICE_TIERS.find(t => t.recommended) || SERVICE_TIERS[1];
}

// For backwards compatibility
export { SERVICE_TIERS as default };
